import React from "react";
import { observer } from "mobx-react";
import { TenantProfileUpdateStore } from "../../store";
import { TenantProfileUpdateForm } from "./TenantProfileUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { PersonType } from "../../../../api/graphql";

export type TenantProfileUpdateProps = {
  tenantProfileUpdateStore: TenantProfileUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantProfileUpdate: React.FC<TenantProfileUpdateProps> = observer(
  ({ tenantProfileUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={tenantProfileUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={tenantProfileUpdateStore.savedButOutdated}
          onReload={() => tenantProfileUpdateStore.reloadTenantProfile()}
          reloadDisabled={tenantProfileUpdateStore.isProcessing}
        />
        <TenantProfileUpdateForm
          tenantProfileUpdateStore={tenantProfileUpdateStore}
          personTypeError={tenantProfileUpdateStore.errors.personType?.message}
          personTypeOptions={tenantProfileUpdateStore.getPersonTypeOptions()}
          onPersonTypeChange={(value: PersonType | "") => {
            tenantProfileUpdateStore.setPersonTypeValue(value);
          }}
          personType={tenantProfileUpdateStore.entityValues.personType}
          legalNameError={tenantProfileUpdateStore.errors.legalName?.message}
          onLegalNameChange={(value: string) => {
            tenantProfileUpdateStore.setLegalNameValue(value);
          }}
          legalName={tenantProfileUpdateStore.entityValues.legalName}
          ownPublisherError={
            tenantProfileUpdateStore.errors.ownPublisher?.message
          }
          onOwnPublisherChange={(value) => {
            tenantProfileUpdateStore.setOwnPublisherValue(value);
          }}
          ownPublisherSelectedValue={
            tenantProfileUpdateStore.entityValues.showOwnPublisher
          }
          onOwnPublisherSelected={(value) => {
            tenantProfileUpdateStore.setSelectedOwnPublisher(value);
          }}
          ownPublisherOptions={tenantProfileUpdateStore.getOwnPublisherOptions()}
          businessIdError={tenantProfileUpdateStore.errors.businessId?.message}
          onBusinessIdChange={(value: string) => {
            tenantProfileUpdateStore.setBusinessIdValue(value);
          }}
          businessId={tenantProfileUpdateStore.entityValues.businessId}
          legalEmailError={tenantProfileUpdateStore.errors.legalEmail?.message}
          onLegalEmailChange={(value: string) => {
            tenantProfileUpdateStore.setLegalEmailValue(value);
          }}
          legalEmail={tenantProfileUpdateStore.entityValues.legalEmail}
          streetAndNumberError={
            tenantProfileUpdateStore.errors.streetAndNumber?.message
          }
          onStreetAndNumberChange={(value: string) => {
            tenantProfileUpdateStore.setStreetAndNumberValue(value);
          }}
          streetAndNumber={
            tenantProfileUpdateStore.entityValues.streetAndNumber
          }
          floorError={tenantProfileUpdateStore.errors.floor?.message}
          onFloorChange={(value: string) => {
            tenantProfileUpdateStore.setFloorValue(value);
          }}
          floor={tenantProfileUpdateStore.entityValues.floor}
          suiteError={tenantProfileUpdateStore.errors.suite?.message}
          onSuiteChange={(value: string) => {
            tenantProfileUpdateStore.setSuiteValue(value);
          }}
          suite={tenantProfileUpdateStore.entityValues.suite}
          postalCodeError={tenantProfileUpdateStore.errors.postalCode?.message}
          onPostalCodeChange={(value: string) => {
            tenantProfileUpdateStore.setPostalCodeValue(value);
          }}
          postalCode={tenantProfileUpdateStore.entityValues.postalCode}
          countryError={tenantProfileUpdateStore.errors.country?.message}
          onCountryChange={(value) => {
            tenantProfileUpdateStore.setCountryValue(value);
          }}
          countrySelectedValue={
            tenantProfileUpdateStore.entityValues.showCountry
          }
          onCountrySelected={(value) => {
            tenantProfileUpdateStore.setSelectedCountry(value);
          }}
          countryOptions={tenantProfileUpdateStore.getCountryOptions()}
          provinceError={tenantProfileUpdateStore.errors.province?.message}
          onProvinceChange={(value) => {
            tenantProfileUpdateStore.setProvinceValue(value);
          }}
          provinceSelectedValue={
            tenantProfileUpdateStore.entityValues.showProvince
          }
          onProvinceSelected={(value) => {
            tenantProfileUpdateStore.setSelectedProvince(value);
          }}
          provinceOptions={tenantProfileUpdateStore.getProvinceOptions()}
          cityError={tenantProfileUpdateStore.errors.city?.message}
          onCityChange={(value: string) => {
            tenantProfileUpdateStore.setCityValue(value);
          }}
          city={tenantProfileUpdateStore.entityValues.city}
          vatStatusError={tenantProfileUpdateStore.errors.vatStatus?.message}
          onVatStatusChange={(value) => {
            tenantProfileUpdateStore.setVatStatusValue(value);
          }}
          vatStatusSelectedValue={
            tenantProfileUpdateStore.entityValues.showVatStatus
          }
          onVatStatusSelected={(value) => {
            tenantProfileUpdateStore.setSelectedVatStatus(value);
          }}
          vatStatusOptions={tenantProfileUpdateStore.getVatStatusOptions()}
          taxIdError={tenantProfileUpdateStore.errors.taxId?.message}
          onTaxIdChange={(value: string) => {
            tenantProfileUpdateStore.setTaxIdValue(value);
          }}
          taxId={tenantProfileUpdateStore.entityValues.taxId}
          grossReceiptsStatusError={
            tenantProfileUpdateStore.errors.grossReceiptsStatus?.message
          }
          onGrossReceiptsStatusChange={(value) => {
            tenantProfileUpdateStore.setGrossReceiptsStatusValue(value);
          }}
          grossReceiptsStatusSelectedValue={
            tenantProfileUpdateStore.entityValues.showGrossReceiptsStatus
          }
          onGrossReceiptsStatusSelected={(value) => {
            tenantProfileUpdateStore.setSelectedGrossReceiptsStatus(value);
          }}
          grossReceiptsStatusOptions={tenantProfileUpdateStore.getGrossReceiptsStatusOptions()}
          grossReceiptsIdError={
            tenantProfileUpdateStore.errors.grossReceiptsId?.message
          }
          onGrossReceiptsIdChange={(value: string) => {
            tenantProfileUpdateStore.setGrossReceiptsIdValue(value);
          }}
          grossReceiptsId={
            tenantProfileUpdateStore.entityValues.grossReceiptsId
          }
          billingEmailError={
            tenantProfileUpdateStore.errors.billingEmail?.message
          }
          onBillingEmailChange={(value: string) => {
            tenantProfileUpdateStore.setBillingEmailValue(value);
          }}
          billingEmail={tenantProfileUpdateStore.entityValues.billingEmail}
          billingCurrencyError={
            tenantProfileUpdateStore.errors.billingCurrency?.message
          }
          onBillingCurrencyChange={(value) => {
            tenantProfileUpdateStore.setBillingCurrencyValue(value);
          }}
          billingCurrencySelectedValue={
            tenantProfileUpdateStore.entityValues.showBillingCurrency
          }
          onBillingCurrencySelected={(value) => {
            tenantProfileUpdateStore.setSelectedBillingCurrency(value);
          }}
          billingCurrencyOptions={tenantProfileUpdateStore.getBillingCurrencyOptions()}
          printerNameError={
            tenantProfileUpdateStore.errors.printerName?.message
          }
          onPrinterNameChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterNameValue(value);
          }}
          printerName={tenantProfileUpdateStore.entityValues.printerName}
          printerStreetAndNumberError={
            tenantProfileUpdateStore.errors.printerStreetAndNumber?.message
          }
          onPrinterStreetAndNumberChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterStreetAndNumberValue(value);
          }}
          printerStreetAndNumber={
            tenantProfileUpdateStore.entityValues.printerStreetAndNumber
          }
          printerFloorError={
            tenantProfileUpdateStore.errors.printerFloor?.message
          }
          onPrinterFloorChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterFloorValue(value);
          }}
          printerFloor={tenantProfileUpdateStore.entityValues.printerFloor}
          printerSuiteError={
            tenantProfileUpdateStore.errors.printerSuite?.message
          }
          onPrinterSuiteChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterSuiteValue(value);
          }}
          printerSuite={tenantProfileUpdateStore.entityValues.printerSuite}
          printerPostalCodeError={
            tenantProfileUpdateStore.errors.printerPostalCode?.message
          }
          onPrinterPostalCodeChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterPostalCodeValue(value);
          }}
          printerPostalCode={
            tenantProfileUpdateStore.entityValues.printerPostalCode
          }
          printerCountryError={
            tenantProfileUpdateStore.errors.printerCountry?.message
          }
          onPrinterCountryChange={(value) => {
            tenantProfileUpdateStore.setPrinterCountryValue(value);
          }}
          printerCountrySelectedValue={
            tenantProfileUpdateStore.entityValues.showPrinterCountry
          }
          onPrinterCountrySelected={(value) => {
            tenantProfileUpdateStore.setSelectedPrinterCountry(value);
          }}
          printerCountryOptions={tenantProfileUpdateStore.getPrinterCountryOptions()}
          printerProvinceError={
            tenantProfileUpdateStore.errors.printerProvince?.message
          }
          onPrinterProvinceChange={(value) => {
            tenantProfileUpdateStore.setPrinterProvinceValue(value);
          }}
          printerProvinceSelectedValue={
            tenantProfileUpdateStore.entityValues.showPrinterProvince
          }
          onPrinterProvinceSelected={(value) => {
            tenantProfileUpdateStore.setSelectedPrinterProvince(value);
          }}
          printerProvinceOptions={tenantProfileUpdateStore.getPrinterProvinceOptions()}
          printerCityError={
            tenantProfileUpdateStore.errors.printerCity?.message
          }
          onPrinterCityChange={(value: string) => {
            tenantProfileUpdateStore.setPrinterCityValue(value);
          }}
          printerCity={tenantProfileUpdateStore.entityValues.printerCity}
          sadaicFtpUrlError={
            tenantProfileUpdateStore.errors.sadaicFtpUrl?.message
          }
          onSadaicFtpUrlChange={(value: string) => {
            tenantProfileUpdateStore.setSadaicFtpUrlValue(value);
          }}
          sadaicFtpUrl={tenantProfileUpdateStore.entityValues.sadaicFtpUrl}
          sadaicFtpUserError={
            tenantProfileUpdateStore.errors.sadaicFtpUser?.message
          }
          onSadaicFtpUserChange={(value: string) => {
            tenantProfileUpdateStore.setSadaicFtpUserValue(value);
          }}
          sadaicFtpUser={tenantProfileUpdateStore.entityValues.sadaicFtpUser}
          sadaicFtpPasswordError={
            tenantProfileUpdateStore.errors.sadaicFtpPassword?.message
          }
          onSadaicFtpPasswordChange={(value: string) => {
            tenantProfileUpdateStore.setSadaicFtpPasswordValue(value);
          }}
          sadaicFtpPassword={
            tenantProfileUpdateStore.entityValues.sadaicFtpPassword
          }
          sadaicEmailError={
            tenantProfileUpdateStore.errors.sadaicEmail?.message
          }
          onSadaicEmailChange={(value: string) => {
            tenantProfileUpdateStore.setSadaicEmailValue(value);
          }}
          sadaicEmail={tenantProfileUpdateStore.entityValues.sadaicEmail}
          recordId={tenantProfileUpdateStore.tenantProfileInstance?.id || ""}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={tenantProfileUpdateStore.saveDisabled}
          onSave={() => tenantProfileUpdateStore.saveTenantProfile()}
          onCancel={() => tenantProfileUpdateStore.cancelTenantProfile()}
        />
        <ErrorDialog
          open={tenantProfileUpdateStore.errorDialogOpened}
          title={tenantProfileUpdateStore.errorTitle}
          description={tenantProfileUpdateStore.errorDescription}
          onClose={() => tenantProfileUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={tenantProfileUpdateStore.confirmDialogOpened}
          title={tenantProfileUpdateStore.confirmDialogTitle}
          description={tenantProfileUpdateStore.confirmDialogDescription}
          onConfirm={() => tenantProfileUpdateStore.executeConfirmedAction()}
          onCancel={() => tenantProfileUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
